import { useEffect, useMemo, useState } from "react";
import {
  areComparableValuesEqual,
  buildEditableBaselineRecord,
  buildEditableEntries,
  buildEditableListItem,
  buildEditableObjectField,
  buildEntryFormValue,
  parseEditedValue,
} from "../utils/editableRecord";
import {
  DEFAULT_DOC_STATUS,
  normalizeDocStatusValue,
} from "../utils/docStatus";
import {
  COMPANY_EDIT_OMITTED_KEYS,
  normalizeCompanyTextList,
} from "../utils/companyEditor";

const useRecordEditor = ({
  datasetKey = "",
  editableRecord,
  onSave,
  saveError = "",
  enableDocStatusWorkflow = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formState, setFormState] = useState({});
  const [companyAliases, setCompanyAliases] = useState([]);
  const [docStatus, setDocStatus] = useState(DEFAULT_DOC_STATUS);
  const [docStatusTouched, setDocStatusTouched] = useState(false);
  const [formError, setFormError] = useState("");
  const [selectedArrayItemByKey, setSelectedArrayItemByKey] = useState({});
  const [selectedObjectFieldByKey, setSelectedObjectFieldByKey] = useState({});

  const editableEntries = useMemo(
    () => buildEditableEntries(editableRecord, datasetKey),
    [editableRecord, datasetKey]
  );
  const companyEditableEntries =
    useMemo(
      () =>
        datasetKey === "company"
          ? editableEntries.filter(
              (entry) =>
                entry.key !== "aliases" && !COMPANY_EDIT_OMITTED_KEYS.has(entry.key)
            )
          : [],
      [datasetKey, editableEntries]
    );

  useEffect(() => {
    const nextFormState = {};
    editableEntries.forEach((entry) => {
      nextFormState[entry.key] = buildEntryFormValue(entry);
    });
    setFormState(nextFormState);
    setCompanyAliases(normalizeCompanyTextList(editableRecord?.aliases));
    setDocStatus(normalizeDocStatusValue(editableRecord?.doc_status));
    setDocStatusTouched(false);
    setFormError("");
    setIsEditing(false);
    setSelectedArrayItemByKey({});
    setSelectedObjectFieldByKey({});
  }, [editableEntries, editableRecord]);

  useEffect(() => {
    if (saveError) {
      setFormError(saveError);
    }
  }, [saveError]);

  const resetEditingState = () => {
    const nextFormState = {};
    editableEntries.forEach((entry) => {
      nextFormState[entry.key] = buildEntryFormValue(entry);
    });
    setFormState(nextFormState);
    setCompanyAliases(normalizeCompanyTextList(editableRecord?.aliases));
    setDocStatus(normalizeDocStatusValue(editableRecord?.doc_status));
    setDocStatusTouched(false);
    setFormError("");
    setIsEditing(false);
    setSelectedArrayItemByKey({});
    setSelectedObjectFieldByKey({});
  };

  useEffect(() => {
    if (isEditing) {
      setFormError("");
    }
  }, [isEditing]);

  const setFormValue = (key, value) => {
    setFormState((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleDocStatusChange = (nextValue) => {
    setDocStatus(normalizeDocStatusValue(nextValue));
    setDocStatusTouched(true);
  };

  const handleArrayItemChange = (entryKey, itemIndex, nextValue) => {
    setFormState((current) => ({
      ...current,
      [entryKey]: (current[entryKey] || []).map((item, index) =>
        index === itemIndex
          ? {
              ...item,
              textValue: nextValue,
            }
          : item
      ),
    }));
  };

  const handleAddArrayItem = (entryKey) => {
    setFormState((current) => ({
      ...current,
      [entryKey]: [...(current[entryKey] || []), buildEditableListItem("")],
    }));
  };

  const handleRemoveArrayItem = (entryKey, itemIndex) => {
    setFormState((current) => ({
      ...current,
      [entryKey]: (current[entryKey] || []).filter((_, index) => index !== itemIndex),
    }));
  };

  const handleObjectFieldChange = (entryKey, fieldIndex, fieldName, nextValue) => {
    setFormState((current) => ({
      ...current,
      [entryKey]: (current[entryKey] || []).map((field, index) =>
        index === fieldIndex
          ? {
              ...field,
              [fieldName]: nextValue,
            }
          : field
      ),
    }));
  };

  const handleAddObjectField = (entryKey) => {
    setFormState((current) => ({
      ...current,
      [entryKey]: [...(current[entryKey] || []), buildEditableObjectField("", "")],
    }));
  };

  const handleRemoveObjectField = (entryKey, fieldIndex) => {
    setFormState((current) => ({
      ...current,
      [entryKey]: (current[entryKey] || []).filter((_, index) => index !== fieldIndex),
    }));
  };

  const handleCompanyAliasChange = (aliasIndex, nextValue) => {
    setCompanyAliases((current) =>
      current.map((item, index) => (index === aliasIndex ? nextValue : item))
    );
  };

  const handleAddCompanyAlias = () => {
    setCompanyAliases((current) => [...current, ""]);
  };

  const handleRemoveCompanyAlias = (aliasIndex) => {
    setCompanyAliases((current) => current.filter((_, index) => index !== aliasIndex));
  };

  const selectArrayItem = (entryKey, index) => {
    setSelectedArrayItemByKey((current) => ({
      ...current,
      [entryKey]: index,
    }));
  };

  const selectObjectField = (entryKey, index) => {
    setSelectedObjectFieldByKey((current) => ({
      ...current,
      [entryKey]: index,
    }));
  };

  const cancelEditing = () => {
    resetEditingState();
  };

  const startEditing = () => {
    setIsEditing(true);
  };

  const buildSaveRecord = () => {
    const currentDocStatus = normalizeDocStatusValue(editableRecord?.doc_status);

    if (datasetKey === "company") {
      const nextRecord = companyEditableEntries.reduce((nextValue, entry) => {
        nextValue[entry.key] = parseEditedValue(entry, formState[entry.key] ?? "");
        return nextValue;
      }, {});
      nextRecord.aliases = normalizeCompanyTextList(companyAliases);
      if (enableDocStatusWorkflow) {
        nextRecord.doc_status = docStatusTouched
          ? normalizeDocStatusValue(docStatus)
          : currentDocStatus === DEFAULT_DOC_STATUS
            ? "Under Review"
            : currentDocStatus;
      }
      return nextRecord;
    }

    const nextRecord = editableEntries.reduce((nextValue, entry) => {
      nextValue[entry.key] = parseEditedValue(entry, formState[entry.key] ?? "");
      return nextValue;
    }, {});

    if (enableDocStatusWorkflow) {
      nextRecord.doc_status = docStatusTouched
        ? normalizeDocStatusValue(docStatus)
        : currentDocStatus === DEFAULT_DOC_STATUS
          ? "Under Review"
          : currentDocStatus;
    }

    return nextRecord;
  };

  const buildBaselineRecord = () => {
    const currentDocStatus = normalizeDocStatusValue(editableRecord?.doc_status);

    if (datasetKey === "company") {
      const baselineRecord = companyEditableEntries.reduce((nextValue, entry) => {
        nextValue[entry.key] = parseEditedValue(entry, buildEntryFormValue(entry));
        return nextValue;
      }, {});
      baselineRecord.aliases = normalizeCompanyTextList(editableRecord?.aliases);
      if (enableDocStatusWorkflow) {
        baselineRecord.doc_status = currentDocStatus;
      }
      return baselineRecord;
    }

    const baselineRecord = buildEditableBaselineRecord(editableEntries);
    if (enableDocStatusWorkflow) {
      baselineRecord.doc_status = currentDocStatus;
    }
    return baselineRecord;
  };

  const buildChangedRecordPayload = (nextRecord, baselineRecord) =>
    Object.entries(nextRecord).reduce((changedRecord, [key, value]) => {
      if (!areComparableValuesEqual(value, baselineRecord[key])) {
        changedRecord[key] = value;
      }
      return changedRecord;
    }, {});

  const save = async () => {
    if (!editableRecord || !onSave) {
      return;
    }

    try {
      const nextRecord = buildSaveRecord();
      const baselineRecord = buildBaselineRecord();
      const changedRecord = buildChangedRecordPayload(nextRecord, baselineRecord);
      setFormError("");

      if (!Object.keys(changedRecord).length) {
        setIsEditing(false);
        setSelectedArrayItemByKey({});
        setSelectedObjectFieldByKey({});
        return;
      }

      await onSave(changedRecord);
      setIsEditing(false);
      setSelectedArrayItemByKey({});
      setSelectedObjectFieldByKey({});
    } catch (saveIssue) {
      setFormError(saveIssue?.message || "Unable to save this record right now.");
    }
  };

  return {
    addCompanyAlias: handleAddCompanyAlias,
    addArrayItem: handleAddArrayItem,
    addObjectField: handleAddObjectField,
    cancelEditing,
    changeCompanyAlias: handleCompanyAliasChange,
    changeField: setFormValue,
    changeDocStatus: handleDocStatusChange,
    changeArrayItem: handleArrayItemChange,
    changeObjectField: handleObjectFieldChange,
    companyAliases,
    companyEditableEntries,
    editableEntries,
    docStatus,
    formError,
    formState,
    isEditing,
    removeArrayItem: handleRemoveArrayItem,
    removeCompanyAlias: handleRemoveCompanyAlias,
    removeObjectField: handleRemoveObjectField,
    resetEditingState,
    save,
    selectArrayItem,
    selectObjectField,
    selectedArrayItemByKey,
    selectedObjectFieldByKey,
    startEditing,
  };
};

export default useRecordEditor;
